import { SupplyComponent,  COMPONENT_KINDS } from '../../../types/SimpleSupplyPoint';
import { generateUniqueId } from '../../../utils/generateUniqueId';
import { FeederFactory } from '../../../factories/FeederFactory';

/**
 * Simple SupplyPoint için event handling ve business logic
 * Legacy SupplyPointService'e çok daha basit alternatif
 */
export class SimpleSupplyPointService {

  /**
   * Load ekleme eventi
   * @param {object} newLoad - Yeni load data
   * @param {object} simpleSupply - Simple supply state
   * @param {Function} dispatch - Redux dispatch
   * @param {object} actions - Redux actions
   */
  static handleLoadAdded(newLoad, simpleSupply, dispatch, actions) {
     
    // Parent component'i bul (genellikle bir panel)
    const parentComponentId = newLoad.parentComponentId || newLoad.housingId;
    const parentComponent = simpleSupply.getComponent(parentComponentId);

    if (!parentComponent) {
      return;
    }

    // FeederFactory ile load feeder oluştur
    const feederId = generateUniqueId("feeder");
    const loadType = newLoad.branchType || 'socket'; // 'socket', 'machine', 'lighting'
    const feederData = FeederFactory.createFeederForLoad(
      loadType,
      newLoad.power || 0,
      parentComponentId,
      newLoad.id,
      {
        id: feederId,
        cable: newLoad.cable,
        vertices: newLoad.vertices
      }
    );

    // Load data'ya branchType ekle (MCB seçimi için gerekli)
    const enhancedLoadData = {
      ...newLoad,
      branchType: newLoad.branchType || 'socket' // default socket
    };

    // Redux action dispatch - artık panel outs'una MCB referansı da eklenecek
    dispatch(actions.addLoadToComponent({
      parentComponentId,
      loadData: enhancedLoadData,
      feederData
    }));

   
  }

  /**
   * Load silme eventi
   * @param {string} loadId - Silinecek load ID
   * @param {object} simpleSupply - Simple supply state
   * @param {Function} dispatch - Redux dispatch
   * @param {object} actions - Redux actions
   */
  static handleLoadRemoved(loadId, simpleSupply, dispatch, actions) {
 
    const loadComponent = simpleSupply.getComponent(loadId);
    if (!loadComponent) return;

    // Parent component'i bul
    const incomingFeeder = simpleSupply.getIncomingFeeders(loadId)[0];
    const parentComponentId = incomingFeeder?.from;

    // Load ve feeder'ını sil
    dispatch(actions.removeComponent(loadId));

    // Parent component'ten upstream chain'i yeniden hesapla
    if (parentComponentId) {
      dispatch(actions.recalculateUpstreamChain(parentComponentId));
    }

   }

  /**
   * Component electrical güncelleme eventi
   * @param {string} componentId - Component ID
   * @param {object} electricalChanges - Electrical değişiklikler
   * @param {object} simpleSupply - Simple supply state
   * @param {Function} dispatch - Redux dispatch
   * @param {object} actions - Redux actions
   */
  static handleElectricalChanged(componentId, electricalChanges, simpleSupply, dispatch, actions) {
 
    // Component'i güncelle
    dispatch(actions.updateComponent({
      id: componentId,
      changes: { electrical: electricalChanges }
    }));

    // Upstream chain'i yeniden hesapla
    dispatch(actions.recalculateUpstreamChain(componentId));
 
  }

  /**
   * Feeder kablo/protection güncelleme eventi
   * @param {string} feederId - Feeder ID
   * @param {object} feederChanges - Feeder değişiklikler
   * @param {Function} dispatch - Redux dispatch
   * @param {object} actions - Redux actions
   */
  static handleFeederChanged(feederId, feederChanges, dispatch, actions) {

    // Feeder'ı güncelle
    dispatch(actions.updateFeeder({
      id: feederId,
      changes: feederChanges
    }));
 
  }

  /**
   * Yeni panel ekleme eventi
   * @param {object} panelData - Panel data
   * @param {string} parentComponentId - Parent component ID
   * @param {object} simpleSupply - Simple supply state
   * @param {Function} dispatch - Redux dispatch
   * @param {object} actions - Redux actions
   */
  static handlePanelAdded(panelData, parentComponentId, simpleSupply, dispatch, actions) {
 

    // Panel component oluştur
    const panelComponent = new SupplyComponent({
      ...panelData,
      kind: panelData.kind || COMPONENT_KINDS.DIST_PANEL,
      parentComponentId:parentComponentId

    });

    // FeederFactory ile panel feeder oluştur
    const feederId = generateUniqueId("feeder");
    const feederData = FeederFactory.createPanelFeeder(
      parentComponentId,
      panelComponent.id,
      {
        id: feederId,
        type: panelData.feederType || "mf-03-b",
        cable: panelData.cable,
        vertices: panelData.vertices
      }
    );

    // Redux'a ekle
    dispatch(actions.addComponent(panelComponent));
    dispatch(actions.addFeeder(feederData));

    // Upstream chain'i yeniden hesapla
    dispatch(actions.recalculateUpstreamChain(parentComponentId));
 
  }

  /**
   * Bulk electrical recalculation
   * @param {object} simpleSupply - Simple supply state
   * @param {Function} dispatch - Redux dispatch
   * @param {object} actions - Redux actions
   */
  static handleBulkRecalculation(simpleSupply, dispatch, actions) {
 
    const startTime = performance.now();

    // Tüm component'leri yeniden hesapla
    dispatch(actions.recalculateAllElectrical());

    const endTime = performance.now();
   }

  /**
   * Component validation
   * @param {object} simpleSupply - Simple supply state
   * @returns {Array} Validation errors
   */
  static validateSupplyPoint(simpleSupply) {
    const errors = [];

    try {
      // Basic validation
      const basicErrors = simpleSupply.validate();
      errors.push(...basicErrors);

      // Business logic validation
      const supplyComponents = simpleSupply.getComponentByKind(COMPONENT_KINDS.SUPPLY);
      if (supplyComponents.length === 0) {
        errors.push("No supply component found");
      } else if (supplyComponents.length > 1) {
        errors.push("Multiple supply components found");
      }

      // Orphaned components check
      simpleSupply.components.forEach(component => {
        if (component.kind !== COMPONENT_KINDS.SUPPLY) {
          const incomingFeeders = simpleSupply.getIncomingFeeders(component.id);
          if (incomingFeeders.length === 0) {
            errors.push(`Orphaned component: ${component.name} (${component.id})`);
          }
        }
      });

      // Circular dependency check
      const visited = new Set();
      const recursionStack = new Set();

      const hasCircularDependency = (componentId) => {
        if (recursionStack.has(componentId)) return true;
        if (visited.has(componentId)) return false;

        visited.add(componentId);
        recursionStack.add(componentId);

        const outgoingFeeders = simpleSupply.getOutgoingFeeders(componentId);
        for (const feeder of outgoingFeeders) {
          if (hasCircularDependency(feeder.to)) return true;
        }

        recursionStack.delete(componentId);
        return false;
      };

      simpleSupply.components.forEach(component => {
        if (hasCircularDependency(component.id)) {
          errors.push(`Circular dependency detected involving component: ${component.name}`);
        }
      });

    } catch (error) {
      errors.push(`Validation error: ${error.message}`);
    }

    return errors;
  }

  /**
   * Performance optimization - identify bottlenecks
   * @param {object} simpleSupply - Simple supply state
   * @returns {object} Performance metrics
   */
  static analyzePerformance(simpleSupply) {
    const metrics = {
      componentCount: simpleSupply.components.length,
      feederCount: simpleSupply.feeders.length,
      maxDepth: 0,
      complexComponents: [],
      performanceScore: 0
    };

    // Calculate max depth
    const calculateDepth = (componentId, visited = new Set()) => {
      if (visited.has(componentId)) return 0;
      visited.add(componentId);

      const outgoingFeeders = simpleSupply.getOutgoingFeeders(componentId);
      let maxChildDepth = 0;

      outgoingFeeders.forEach(feeder => {
        const childDepth = calculateDepth(feeder.to, new Set(visited));
        maxChildDepth = Math.max(maxChildDepth, childDepth);
      });

      return maxChildDepth + 1;
    };

    const supplyComponents = simpleSupply.getComponentByKind(COMPONENT_KINDS.SUPPLY);
    if (supplyComponents.length > 0) {
      metrics.maxDepth = calculateDepth(supplyComponents[0].id);
    }

    // Identify complex components (many connections)
    simpleSupply.components.forEach(component => {
      const totalConnections = component.incomingFeeders.length + component.outgoingFeeders.length;
      if (totalConnections > 5) {
        metrics.complexComponents.push({
          id: component.id,
          name: component.name,
          connections: totalConnections
        });
      }
    });

    // Calculate performance score (0-100)
    const baseScore = 100;
    const componentPenalty = Math.max(0, (metrics.componentCount - 50) * 0.5);
    const depthPenalty = Math.max(0, (metrics.maxDepth - 5) * 5);
    const complexityPenalty = metrics.complexComponents.length * 2;

    metrics.performanceScore = Math.max(0, baseScore - componentPenalty - depthPenalty - complexityPenalty);

    return metrics;
  }

  // ===============================
  // HELPER METHODS
  // ===============================

  /**
   * Güçten akım hesaplama
   * @param {number} power - Güç (W)
   * @param {number} phaseCount - Faz sayısı
   * @returns {number} Akım (A)
   */
  static calculateCurrentFromPower(power, phaseCount = 3) {
    if (!power || power <= 0) return 0;
    const voltage = phaseCount === 3 ? 380 : 220;
    return power / voltage;
  }

  /**
   * Akımdan güç hesaplama
   * @param {number} current - Akım (A)
   * @param {number} phaseCount - Faz sayısı
   * @returns {number} Güç (W)
   */
  static calculatePowerFromCurrent(current, phaseCount = 3) {
    if (!current || current <= 0) return 0;
    const voltage = phaseCount === 3 ? 380 : 220;
    return current * voltage;
  }

  /**
   * Component path calculation
   * @param {string} fromComponentId - Başlangıç component
   * @param {string} toComponentId - Hedef component
   * @param {object} simpleSupply - Simple supply state
   * @returns {Array} Path of component IDs
   */
  static findComponentPath(fromComponentId, toComponentId, simpleSupply) {
    const visited = new Set();
    const queue = [{ id: fromComponentId, path: [fromComponentId] }];

    while (queue.length > 0) {
      const { id, path } = queue.shift();

      if (id === toComponentId) {
        return path;
      }

      if (visited.has(id)) continue;
      visited.add(id);

      const outgoingFeeders = simpleSupply.getOutgoingFeeders(id);
      outgoingFeeders.forEach(feeder => {
        if (!visited.has(feeder.to)) {
          queue.push({
            id: feeder.to,
            path: [...path, feeder.to]
          });
        }
      });
    }

    return []; // Path not found
  }

  /**
   * Generate component summary for debugging
   * @param {object} simpleSupply - Simple supply state
   * @returns {object} Summary object
   */
  static generateSummary(simpleSupply) {
    const summary = {
      totalComponents: simpleSupply.components.length,
      totalFeeders: simpleSupply.feeders.length,
      componentsByKind: {},
      feedersByType: {},
      totalInstalledPower: 0,
      totalDemandPower: 0,
      errors: this.validateSupplyPoint(simpleSupply)
    };

    // Group by kind
    Object.values(COMPONENT_KINDS).forEach(kind => {
      summary.componentsByKind[kind] = simpleSupply.getComponentByKind(kind).length;
    });

    // Group by type
    const feederTypes = [...new Set(simpleSupply.feeders.map(f => f.type))];
    feederTypes.forEach(type => {
      summary.feedersByType[type] = simpleSupply.getFeedersByType(type).length;
    });

    // Calculate totals
    simpleSupply.components.forEach(component => {
      summary.totalInstalledPower += component.electrical.installedPower || 0;
      summary.totalDemandPower += component.electrical.demandPower || 0;
    });

    return summary;
  }
}